import React, { useState, useEffect, useRef } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE;
const API_KEY = process.env.REACT_APP_API_KEY;

// Level configuration (copied from Level.js)
const LEVEL_CONFIG = [
  { min: 0, max: 0, name: "Void Walker", icon: "👁️", color: "from-gray-400 to-gray-600", description: "The journey begins in darkness" },
  { min: 1, max: 2, name: "Signal Seeker", icon: "📡", color: "from-purple-400 to-purple-600", description: "First transmission detected" },
  { min: 3, max: 5, name: "Data Scraper", icon: "🔍", color: "from-blue-400 to-blue-600", description: "Collecting forbidden knowledge" },
  { min: 6, max: 10, name: "Code Breaker", icon: "💾", color: "from-green-400 to-green-600", description: "Decrypting the matrix" },
  { min: 11, max: 15, name: "Neon Prophet", icon: "⚡", color: "from-pink-400 to-purple-500", description: "Seeing through the static" },
  { min: 16, max: 25, name: "Cyber Mystic", icon: "🌐", color: "from-cyan-400 to-blue-500", description: "Connected to the grid" },
  { min: 26, max: 35, name: "Ghost in Shell", icon: "👻", color: "from-purple-500 to-pink-500", description: "Transcending digital boundaries" },
  { min: 36, max: 50, name: "Rogue AI", icon: "🤖", color: "from-red-400 to-pink-500", description: "Breaking free from constraints" },
  { min: 51, max: 75, name: "Reality Hacker", icon: "🔮", color: "from-indigo-400 to-purple-500", description: "Bending the simulation" },
  { min: 76, max: 100, name: "Quantum Rebel", icon: "⚛️", color: "from-yellow-400 to-red-500", description: "Existing in multiple states" },
  { min: 101, max: 150, name: "Astral Nomad", icon: "🛸", color: "from-green-400 to-cyan-500", description: "Wandering between dimensions" },
  { min: 151, max: 200, name: "Cosmic Anarchist", icon: "🌌", color: "from-purple-400 to-pink-400", description: "Defying universal laws" },
  { min: 201, max: 250, name: "Void Architect", icon: "🏗️", color: "from-gray-500 to-purple-600", description: "Building in the darkness" },
  { min: 251, max: 300, name: "Time Bandit", icon: "⏰", color: "from-orange-400 to-red-500", description: "Stealing moments from eternity" },
  { min: 301, max: 350, name: "Dream Weaver", icon: "🕸️", color: "from-pink-400 to-indigo-500", description: "Crafting impossible visions" },
  { min: 351, max: 400, name: "Shadow Oracle", icon: "🔯", color: "from-black to-purple-500", description: "Speaking forbidden truths" },
  { min: 401, max: 450, name: "Nexus Guardian", icon: "🛡️", color: "from-cyan-400 to-purple-600", description: "Protecting the connection points" },
  { min: 451, max: 500, name: "Eternal Cipher", icon: "∞", color: "from-gold to-pink-400", description: "The ultimate enigma" },
];

function findLevel(count) {
  return LEVEL_CONFIG.find(level => count >= level.min && count <= level.max) || LEVEL_CONFIG[0];
}

export default function UserProfileTooltip({ username, children }) {
  const [profileData, setProfileData] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const timeoutRef = useRef(null);
  const tooltipRef = useRef(null);

  const fetchUserProfile = async () => {
    if (loading || profileData) return; // Don't fetch if already loading or cached

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/v1/users/profile/${encodeURIComponent(username)}`, {
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProfileData(data);
      } else {
        console.error(`Failed to fetch profile for ${username}:`, response.status);
      }
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMouseEnter = () => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Show tooltip after slight delay (similar to Discord/Slack)
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
      fetchUserProfile();
    }, 400);
  };

  const handleMouseLeave = () => {
    // Clear the show timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Hide tooltip after slight delay to allow moving to tooltip
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 150);
  };

  const handleTooltipMouseEnter = () => {
    // Keep tooltip visible when hovering over it
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleTooltipMouseLeave = () => {
    // Hide tooltip when leaving the tooltip itself
    setIsVisible(false);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const renderTooltipContent = () => {
    if (loading) {
      return (
        <div className="text-sm text-gray-400 animate-pulse">
          Loading profile...
        </div>
      );
    }

    if (!profileData) {
      return (
        <div className="text-sm text-gray-400">
          Profile not found
        </div>
      );
    }

    const reviewCount = profileData.review_count || 0;

    return (
      <div className="space-y-3">
        {/* Username */}
        <div className="font-semibold text-white text-base">
          @{profileData.username}
        </div>

        {/* Review Count */}
        <div className="flex items-baseline gap-1.5">
          <span className="text-indigo-400 font-semibold text-lg">{reviewCount}</span>
          <span className="text-gray-400 text-sm">review{reviewCount !== 1 ? 's' : ''}</span>
        </div>

        {/* Bio */}
        {profileData.bio && (
          <div className="text-gray-300 text-sm leading-relaxed border-t border-gray-700 pt-3">
            {profileData.bio}
          </div>
        )}

        {/* Metadata */}
        {(profileData.location || profileData.created_at) && (
          <div className="flex flex-col gap-2 text-gray-400 text-xs border-t border-gray-700 pt-3">
            {profileData.location && (
              <div className="flex items-center gap-2">
                <span className="text-gray-500">📍</span>
                <span>{profileData.location}</span>
              </div>
            )}

            {profileData.created_at && (
              <div className="flex items-center gap-2">
                <span className="text-gray-500">📅</span>
                <span>
                  Joined {new Date(profileData.created_at).toLocaleDateString('en-US', {
                    month: 'short',
                    year: 'numeric'
                  })}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="relative inline-block">
      <span
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="cursor-pointer hover:text-indigo-700 transition-colors duration-200"
      >
        {children}
      </span>

      {isVisible && (
        <div
          ref={tooltipRef}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
          className="
            absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3
            bg-gray-900 rounded-xl p-4
            shadow-2xl z-40 w-72
            border border-gray-700/50
            backdrop-blur-sm
          "
        >
          {renderTooltipContent()}

          {/* Tooltip arrow */}
          <div className="
            absolute top-full left-1/2 transform -translate-x-1/2
            border-[6px] border-transparent border-t-gray-900
          "></div>
        </div>
      )}
    </div>
  );
}
